import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { pmGet } from '../api';
import {
  Bubble, Cols, Donut, Funnel, HBars, Heat, Kpi, Ring, SCurve, Stack, Waterfall,
  TCOL, TYPES, daysBetween, fmtNum, kmoney, money, util,
} from './exdCharts';
import './ExecutionDashboard.css';

/**
 * Execution Dashboard — §17's rebuild.
 *
 * Cloned from `ai_readiness_template/views/execution-dashboard.ejs`. Six tabs, six questions,
 * each with ITS OWN KPI row — repeating one strip across tabs is what makes a dashboard feel
 * shallow (§17.3).
 *
 * ── THE TWO KINDS OF FILTER, AND WHY THEY DIFFER (§17.4) ───────────────────────────────
 *
 * YEAR and DELIVERY PROJECT are server-side: they change what is FETCHED, they live in the
 * query string, and a filtered view is therefore a link somebody can bookmark. Converting them
 * to client-side filtering would feel snappier and would break every saved link.
 *
 * LIFECYCLE STAGE and HEALTH are client-side: they refine data already in the page, so they
 * cost nothing and need no round trip.
 *
 * Every filter RE-ROLLS THE WHOLE PORTFOLIO OBJECT, so the KPIs, the charts and the tab counts
 * all move together. A number that stayed still while its neighbours moved would read as a bug
 * whether or not it was one.
 */

type Dict = Record<string, unknown>;

interface ProjectRow {
  id: string;
  description: string | null;
  methodology: string | null;
  finalized: boolean;
  start_date: string | null;
  opps: number;
  stage: string;
  completion: number;
  inScope: number;
  done: number;
  overdue: number;
  blocked: number;
  deliverables: number;
  activities: number;
  unclassified: number;
  statusCounts: Record<string, number>;
  budgetPlanned: number;
  budgetActual: number;
  budgetUtil: number;
  variance: number;
  effortPlanned: number;
  effortActual: number;
  effortUtil: number;
  openRisks: number;
  criticalRisks: number;
  actions: number;
  actionsOverdue: number;
  nextMilestone: { date: string; label: string } | null;
  status: 'Complete' | 'At Risk' | 'On Track';
}

interface Series {
  wiByPhaseType: Dict[];
  wiByStatusType: Dict[];
  phaseProgress: { phase: string; label: string; n: number; avg: number }[];
  wiCurve: { ym: string; n: number; done: number }[];
  phaseSpend: Dict[];
  wiOverdue: { name: string; due: string; id: string; wtype: string; status: string; progress: number; owner: string }[];
  wiLateBuckets: { bucket: string; n: number }[];
  actions: { title: string; atype: string; status: string; priority: string; owner: string; due: string | null; pct: number; id: number }[];
  risks: { id: string; sev: string; status: string }[];
}

const STAGES = ['Created', 'Configured', 'Scoped', 'Finalized', 'In flight', 'Complete'];
const STAGE_COL = ['#b0bec5', '#78909c', '#5c6bc0', '#1976d2', '#00897b', '#2e7d32'];
const todayISO = () => new Date().toISOString().slice(0, 10);

const TABS = [
  { id: 'portfolio', label: 'Portfolio', icon: 'donut_large' },
  { id: 'work', label: 'Work Items', icon: 'fact_check' },
  { id: 'schedule', label: 'Schedule', icon: 'schedule' },
  { id: 'money', label: 'Money & Effort', icon: 'payments' },
  { id: 'actions', label: 'Actions & Risks', icon: 'flag' },
  { id: 'projects', label: 'Projects', icon: 'table_rows' },
] as const;
type TabId = (typeof TABS)[number]['id'];

const HELP: HelpContent = {
  title: 'Execution Dashboard',
  icon: '🚀',
  purpose:
    'The <strong>Execution Dashboard</strong> is the portfolio-level view of <strong>delivery health</strong> across every delivery project &mdash; not only the finalized ones. Six tabs answer six different questions: where the portfolio stands, what the <strong>work items</strong> are, what has <strong>slipped</strong>, where the <strong>money and effort</strong> went, what <strong>actions and risks</strong> are open, and how each project compares.',
  doHere: [
    'Read the <strong>Portfolio</strong> tab first &mdash; the KPI strip, the <strong>planned vs. delivered</strong> curve and the <strong>delivery vs. spend</strong> bubble chart give portfolio status in one screen.',
    'Use <strong>Work Items</strong> to see the split between <strong>deliverables</strong> (which produce an artefact) and <strong>activities</strong> (which do not), by phase and by status.',
    'Use <strong>Schedule</strong> to see how far behind the overdue work actually is &mdash; a week late and a quarter late are different problems.',
    'Use <strong>Money &amp; Effort</strong> to see which project drives the variance, and <strong>Actions &amp; Risks</strong> to see who is carrying open follow-up.',
    'Filter by <strong>Year</strong> and <strong>Project</strong> (these reload and can be bookmarked), then narrow further by <strong>Lifecycle Stage</strong> and <strong>Health</strong> without leaving the screen.',
  ],
  fits: 'Discover &rarr; Score &rarr; Finance &rarr; Optimize &rarr; Plan &rarr; <strong>Execute (this view)</strong> &rarr; Monitor',
  firstTime:
    'Leave every filter on its default and read <strong>Overall completion</strong>, <strong>At risk</strong> and <strong>Overdue work</strong> on the Portfolio tab. Then open <strong>Schedule</strong> to see what is actually late.',
  sections: [
    {
      label: 'Delivery project lifecycle stages',
      type: 'defs',
      items: [
        { term: 'Created', color: '#b0bec5', def: 'The delivery project exists but has no methodology yet &mdash; nothing can be planned against it.' },
        { term: 'Configured', color: '#78909c', def: 'A methodology (Waterfall or Agile) has been chosen.' },
        { term: 'Scoped', color: '#5c6bc0', def: 'Opportunities have been attached, so there is work to deliver.' },
        { term: 'Finalized', color: '#1976d2', def: 'The timeline is locked on Project Timeline Finalization.' },
        { term: 'In flight', color: '#00897b', def: 'Finalized and work has started &mdash; progress is being recorded.' },
        { term: 'Complete', color: '#2e7d32', def: 'Every work item in scope is done.' },
      ],
      note: 'Stage is <em>derived</em>, not stored. Projects at any stage appear here &mdash; a project that has been created but not configured is visible rather than silently dropped.',
    },
    {
      label: 'Work item types',
      type: 'defs',
      items: [
        { term: 'Deliverable', color: '#1976d2', def: 'Work that produces an artefact &mdash; a document, a build, a signed-off decision.' },
        { term: 'Activity', color: '#c2185b', def: 'Work that must happen but leaves no artefact &mdash; a workshop, a review, a cutover rehearsal.' },
        { term: 'Unclassified', color: '#b0bec5', def: 'No type has been set yet. These are <strong>counted, never hidden</strong> &mdash; set the type on Work Item Definition.' },
      ],
    },
    {
      label: 'Budget utilization colors',
      type: 'html',
      html: "Utilization is <strong>actual &divide; planned</strong> spend.<div style='margin-top:10px; font-size:12.5px; color:#546e7a; line-height:1.9;'><span style='color:#43a047; font-weight:700;'>Green</span> under 85% &mdash; healthy &nbsp;&middot;&nbsp; <span style='color:#fb8c00; font-weight:700;'>Amber</span> 85&ndash;100% &mdash; watch &nbsp;&middot;&nbsp; <span style='color:#e53935; font-weight:700;'>Red</span> over 100% &mdash; overrun</div>",
    },
  ],
  links: [
    { label: 'Portfolio Exceptions — what needs attention this week', href: '/project_management/portfolio/portfolio-oversight/portfolio-exceptions' },
    { label: 'Execution Control Center →', href: '/project_management/program-management/delivery-execution/execution-control-center' },
  ],
};

export function ExecutionDashboard() {
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [series, setSeries] = useState<Series | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [allProjects, setAllProjects] = useState<{ id: string; description: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* Server-side filters — these live in the URL. */
  const [year, setYear] = useState('all');
  const [picked, setPicked] = useState<string[]>([]);
  const [ddOpen, setDdOpen] = useState(false);
  /* Client-side filters — these refine what is already here. */
  const [stageSel, setStageSel] = useState('all');
  const [healthSel, setHealthSel] = useState('all');

  const [tab, setTab] = useState<TabId>('portfolio');
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [pill, setPill] = useState<{ w: number; x: number }>({ w: 0, x: 0 });
  const ddWrap = useRef<HTMLDivElement | null>(null);

  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);
  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  const load = useCallback((y: string, ids: string[]) => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (y && y !== 'all') qs.set('year', y);
    if (ids.length) qs.set('projects', ids.join(','));
    pmGet<{ success: boolean; projects: ProjectRow[]; series: Series; availableYears: number[]; allProjects: { id: string; description: string | null }[]; error?: string }>(
      `/execution-dashboard${qs.toString() ? `?${qs}` : ''}`,
    )
      .then((d) => {
        if (!d.success) throw new Error(d.error ?? 'failed');
        setProjects(d.projects ?? []);
        setSeries(d.series);
        setAvailableYears(d.availableYears ?? []);
        setAllProjects(d.allProjects ?? []);
        setError(null);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load('all', []);
  }, [load]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ddWrap.current && !ddWrap.current.contains(e.target as Node)) setDdOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  /* The sliding pill follows the active tab. Measured, never assumed. */
  useEffect(() => {
    const btn = tabRefs.current[tab];
    if (btn) setPill({ w: btn.offsetWidth, x: btn.offsetLeft });
  }, [tab, projects.length, loading]);

  /*
   * §17.4 — THE RE-ROLL. Client-side filters narrow the project list, and everything the six
   * tabs read is recomputed from that list, so no number can lag behind its neighbours.
   */
  const P = useMemo(() => {
    let ps = projects;
    if (stageSel !== 'all') ps = ps.filter((p) => p.stage === stageSel);
    if (healthSel !== 'all') ps = ps.filter((p) => p.status === healthSel);
    const t = ps.reduce((s, p) => s + p.inScope, 0);
    const sum = (f: (p: ProjectRow) => number) => ps.reduce((s, p) => s + f(p), 0);
    return {
      projects: ps,
      o: {
        count: ps.length,
        finalized: ps.filter((p) => p.finalized).length,
        notFinalized: ps.filter((p) => !p.finalized).length,
        noMethodology: ps.filter((p) => !p.methodology).length,
        onTrack: ps.filter((p) => p.status !== 'At Risk').length,
        atRisk: ps.filter((p) => p.status === 'At Risk').length,
        completion: t ? Math.round(sum((p) => p.completion * p.inScope) / t) : 0,
        wiTotal: t,
        wiDone: sum((p) => p.done),
        deliverables: sum((p) => p.deliverables),
        activities: sum((p) => p.activities),
        unclassified: sum((p) => p.unclassified),
        blocked: sum((p) => p.blocked),
        overdue: sum((p) => p.overdue),
        planned: sum((p) => p.budgetPlanned),
        actual: sum((p) => p.budgetActual),
        effortPlanned: Math.round(sum((p) => p.effortPlanned) * 10) / 10,
        effortActual: Math.round(sum((p) => p.effortActual) * 10) / 10,
        actions: sum((p) => p.actions),
        actionsOverdue: sum((p) => p.actionsOverdue),
        openRisks: sum((p) => p.openRisks),
        critical: sum((p) => p.criticalRisks),
      },
    };
  }, [projects, stageSel, healthSel]);

  const o = P.o;
  const bu = o.planned ? Math.round((o.actual / o.planned) * 100) : 0;
  const eu = o.effortPlanned ? Math.round((o.effortActual / o.effortPlanned) * 100) : 0;
  const today = todayISO();

  const bubbleRows = P.projects.map((p) => ({
    id: p.id, completion: p.completion, budgetUtil: p.budgetPlanned ? p.budgetUtil : null,
    wiTotal: p.inScope, status: p.status,
  }));

  const chipFor = (id: TabId) =>
    id === 'portfolio' || id === 'projects' ? String(o.count)
      : id === 'work' ? String(o.wiTotal)
      : id === 'schedule' ? String(o.overdue)
      : id === 'money' ? kmoney(o.planned)
      : String(o.actions + o.openRisks);

  const s = series;

  return (
    <div className="exd" ref={setRootEl} style={rootH ? { height: rootH } : undefined}>
      <div className="exd-head">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 className="page-header__title">Execution Dashboard</h1>
            <p>Portfolio-level delivery health across every delivery project.</p>
          </div>
          <HelpPanel help={HELP} />
          <div className="exd-filters">
            <div className="ff">
              <label htmlFor="exdYear">Year</label>
              <select id="exdYear" value={year} onChange={(e) => { setYear(e.target.value); load(e.target.value, picked); }}>
                <option value="all">All Years</option>
                {availableYears.map((y) => <option key={y} value={String(y)}>{y}</option>)}
              </select>
            </div>
            <div className="ff">
              <label>
                Project <span className="hint">(pick several)</span>
              </label>
              <div style={{ position: 'relative' }} ref={ddWrap}>
                <button type="button" className="exd-proj-toggle" onClick={() => setDdOpen((v) => !v)}>
                  {picked.length ? `${picked.length} selected` : 'All Projects'} ▾
                </button>
                {ddOpen ? (
                  <div className="exd-proj-panel">
                    {/*
                      CTRL-CLICK, NOT CHECKBOXES. A plain click picks just that project;
                      Ctrl-click (Cmd on a Mac) adds or removes. The same rule the shared
                      Project field uses, so every multi-select on the platform behaves alike.
                    */}
                    {allProjects.map((ap) => {
                      const on = picked.includes(ap.id);
                      return (
                        <div
                          className={`exd-dd-item${on ? ' on' : ''}`}
                          key={ap.id}
                          role="option"
                          aria-selected={on}
                          onClick={(e) =>
                            setPicked((prev) =>
                              e.ctrlKey || e.metaKey
                                ? on
                                  ? prev.filter((x) => x !== ap.id)
                                  : [...prev, ap.id]
                                : [ap.id],
                            )
                          }
                        >
                          <span className="exd-dd-tick">{on ? '✓' : ''}</span>
                          <span>
                            <strong>{ap.id}</strong>
                            {ap.description ? ` - ${ap.description}` : ''}
                          </span>
                        </div>
                      );
                    })}
                    {/* Apply is deliberate: ticking a box does NOT reload, so several
                        projects can be chosen in one go and fetched once. Select all fills the
                        list without reloading either — it is Apply that commits. */}
                    <div style={{ display: 'flex', gap: 10, padding: '8px 10px' }}>
                      <button type="button" className="exd-proj-toggle" style={{ minWidth: 0, height: 30 }}
                        onClick={() => { setDdOpen(false); load(year, picked); }}>Apply</button>
                      <button type="button" className="exd-proj-toggle" style={{ minWidth: 0, height: 30 }}
                        onClick={() => setPicked(allProjects.map((ap) => ap.id))}>Select all</button>
                      <button type="button" className="exd-proj-toggle" style={{ minWidth: 0, height: 30 }}
                        onClick={() => { setPicked([]); setDdOpen(false); load(year, []); }}>Clear</button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="ff">
              <label htmlFor="exdStage">Lifecycle Stage</label>
              <select id="exdStage" value={stageSel} onChange={(e) => setStageSel(e.target.value)}>
                <option value="all">All stages</option>
                {STAGES.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>
            <div className="ff">
              <label htmlFor="exdHealth">Health</label>
              <select id="exdHealth" value={healthSel} onChange={(e) => setHealthSel(e.target.value)}>
                <option value="all">All</option>
                <option value="On Track">On Track</option>
                <option value="At Risk">At Risk</option>
                <option value="Complete">Complete</option>
              </select>
            </div>
          </div>
        </div>

        <div className="exd-tabwrap">
          <div className="exd-tabs">
            <div className="exd-pill" id="exdPill" style={{ width: pill.w, transform: `translateX(${pill.x}px)` }} />
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                ref={(el) => { tabRefs.current[t.id] = el; }}
                className={`exd-tab${tab === t.id ? ' on' : ''}`}
                onClick={() => setTab(t.id)}
              >
                <i className="material-icons">{t.icon}</i>
                <span className="lbl" data-label={t.label}>{t.label}</span>
                <span className="chip">{chipFor(t.id)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="exd-body">
        {error ? <div style={{ color: '#c62828', fontSize: 13, marginBottom: 12 }}>{error}</div> : null}
        {loading && !s ? (
          <div className="exd-empty">Loading…</div>
        ) : !P.projects.length || !s ? (
          <div className="exd-empty">
            <i className="material-icons" style={{ fontSize: 54, color: '#cfd8dc' }}>insights</i>
            <p>
              {!allProjects.length
                ? 'No delivery projects yet. Create one on Project Creation to populate this dashboard.'
                : 'No projects match the selected filters. Adjust the filters above.'}
            </p>
          </div>
        ) : (
          <>
            {/* ── Portfolio ─────────────────────────────────────────────────────────────── */}
            <div className={`exd-panel${tab === 'portfolio' ? ' on' : ''}`}>
              <div className="exd-kpis">
                <Kpi lbl="Delivery projects" val={o.count} sub={`${o.finalized} finalized · ${o.notFinalized} not yet`} c="var(--accent)" />
                <Kpi lbl="Overall completion" val={`${o.completion}%`} sub={`${o.wiDone} of ${o.wiTotal} work items done`} c="var(--good)" />
                <Kpi lbl="At risk" val={o.atRisk} sub={`${o.onTrack} on track`} c={o.atRisk ? 'var(--bad)' : 'var(--good)'} />
                <Kpi lbl="Work items" val={o.wiTotal} sub={`${o.deliverables} deliv · ${o.activities} act · ${o.unclassified} ?`} c="var(--deliv)" />
                <Kpi lbl="Overdue work" val={o.overdue} sub={`${o.blocked} blocked`} c={o.overdue ? 'var(--bad)' : 'var(--good)'} />
                <Kpi lbl="Budget used" val={`${bu}%`} sub={`${kmoney(o.actual)} of ${kmoney(o.planned)}`} c={util(o.planned ? bu : null)} />
                <Kpi lbl="Open actions" val={o.actions} sub={`${o.actionsOverdue} overdue`} c={o.actionsOverdue ? 'var(--warn)' : 'var(--good)'} />
                <Kpi lbl="Open risks" val={o.openRisks} sub={`${o.critical} high / critical`} c={o.critical ? 'var(--bad)' : 'var(--warn)'} />
              </div>
              <div className="exd-grid exd-g2">
                <div className="exd-card">
                  <h3>Planned vs. delivered</h3>
                  <div className="cap">Cumulative work items due by month (grey) against those actually finished (green). The gap is the backlog.</div>
                  <div className="plot"><SCurve rows={s.wiCurve} w={560} h={230} /></div>
                  <div className="exd-legend">
                    <span><i style={{ background: '#90a4ae' }} />Cumulative due</span>
                    <span><i style={{ background: '#2e7d32' }} />Cumulative done</span>
                  </div>
                </div>
                <div className="exd-card">
                  <h3>Delivery vs. spend</h3>
                  <div className="cap">Each bubble is a project, sized by work items. Above the dashed diagonal means money is going out faster than work is coming in; the pink band is over budget.</div>
                  <div className="plot"><Bubble rows={bubbleRows} w={560} h={230} /></div>
                  <div className="exd-legend">
                    <span><i style={{ background: '#1976d2' }} />On track</span>
                    <span><i style={{ background: '#c62828' }} />At risk</span>
                    <span><i style={{ background: '#2e7d32' }} />Complete</span>
                  </div>
                </div>
              </div>
              <div className="exd-grid exd-g3">
                <div className="exd-card">
                  <h3>Delivery project lifecycle</h3>
                  <div className="cap">Every project, not only the finalized ones.</div>
                  <Funnel stages={STAGES.map((k, i) => ({ k, n: P.projects.filter((p) => p.stage === k).length, c: STAGE_COL[i]! }))} />
                  {o.noMethodology ? (
                    <div className="exd-flag">
                      <b>{o.noMethodology}</b> project(s) have no methodology set. Pick one on Project Creation before they can be planned.
                    </div>
                  ) : null}
                </div>
                <div className="exd-card">
                  <h3>Work item mix</h3>
                  <div className="cap">Deliverables produce an artefact; activities do not.</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                    <Donut
                      parts={[
                        { k: 'Deliverable', v: o.deliverables, c: TCOL.Deliverable! },
                        { k: 'Activity', v: o.activities, c: TCOL.Activity! },
                        { k: 'Unclassified', v: o.unclassified, c: TCOL.Unclassified! },
                      ]}
                      size={142}
                      hole={0.62}
                      centre={{ top: o.wiTotal, bot: 'work items' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {[
                        { k: 'Deliverable', v: o.deliverables, c: TCOL.Deliverable! },
                        { k: 'Activity', v: o.activities, c: TCOL.Activity! },
                        { k: 'Unclassified', v: o.unclassified, c: TCOL.Unclassified! },
                      ].map((p) => (
                        <div className="exd-row" key={p.k}>
                          <span>
                            <i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: p.c, marginRight: 7 }} />
                            {p.k}
                          </span>
                          <b className="num">
                            {p.v} <span style={{ color: '#b0bec5', fontWeight: 500 }}>({Math.round((p.v / (o.wiTotal || 1)) * 100)}%)</span>
                          </b>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* §17.6 — counted, never hidden, and it says where to fix them. */}
                  {o.unclassified ? (
                    <div className="exd-flag">
                      <b>{o.unclassified}</b> work items have no type yet. Counted, never hidden — set the type on Work Item Definition.
                    </div>
                  ) : null}
                </div>
                <div className="exd-card">
                  <h3>Completion by project</h3>
                  <div className="cap">Weighted by work items, so a big project cannot hide behind a small one.</div>
                  {P.projects.map((p) => (
                    <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '1fr 96px', gap: 10, marginBottom: 11 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: '#263238', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.id}</div>
                        <div className="exd-bar-tr" style={{ height: 12 }}>
                          <div className="exd-bar-fl" style={{ width: `${p.completion}%`, background: p.status === 'At Risk' ? 'var(--bad)' : p.completion >= 100 ? 'var(--good)' : 'var(--accent)' }} />
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <b className="num" style={{ fontSize: 13 }}>{p.completion}%</b>
                        <div style={{ fontSize: 10.5, color: '#90a4ae' }} className="num">{p.done}/{p.inScope}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Work Items ────────────────────────────────────────────────────────────── */}
            <div className={`exd-panel${tab === 'work' ? ' on' : ''}`}>
              <div className="exd-kpis">
                <Kpi lbl="In scope" val={o.wiTotal} sub={`across ${P.projects.filter((p) => p.inScope).length} projects`} c="var(--accent)" />
                <Kpi lbl="Deliverables" val={o.deliverables} sub="produce an artefact" c="var(--deliv)" />
                <Kpi lbl="Activities" val={o.activities} sub="work without an artefact" c="var(--activ)" />
                <Kpi lbl="Unclassified" val={o.unclassified} sub={`${Math.round((o.unclassified / (o.wiTotal || 1)) * 100)}% of the portfolio`} c={o.unclassified ? 'var(--warn)' : 'var(--good)'} />
                <Kpi lbl="Done" val={o.wiDone} sub={`${o.completion}% average progress`} c="var(--good)" />
                <Kpi lbl="Blocked" val={o.blocked} sub="stalled on a dependency" c={o.blocked ? 'var(--bad)' : 'var(--good)'} />
              </div>
              <div className="exd-grid exd-g2">
                <div className="exd-card">
                  <h3>By phase and type</h3>
                  <div className="cap">Where the work sits in the delivery lifecycle.</div>
                  <div className="plot"><Stack rows={s.wiByPhaseType} keys={TYPES} colours={TCOL} w={300} labelW={140} /></div>
                  <div className="exd-legend">{TYPES.map((t) => <span key={t}><i style={{ background: TCOL[t] }} />{t}</span>)}</div>
                </div>
                <div className="exd-card">
                  <h3>Progress by type</h3>
                  <div className="cap">Are deliverables and activities moving at the same rate?</div>
                  <div className="plot"><Stack rows={s.wiByStatusType} keys={TYPES} colours={TCOL} w={300} labelW={110} /></div>
                  <div className="exd-legend">{TYPES.map((t) => <span key={t}><i style={{ background: TCOL[t] }} />{t}</span>)}</div>
                </div>
              </div>
              <div className="exd-card">
                <h3>Average progress by phase</h3>
                <div className="cap">Each ring is the mean progress of every work item in that phase; the caption is how many.</div>
                <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', justifyContent: 'space-around', padding: '6px 0' }}>
                  {s.phaseProgress.map((ph) => (
                    <Ring key={ph.phase} pct={ph.avg} size={96} colour={ph.avg >= 75 ? '#2e7d32' : ph.avg >= 25 ? '#1976d2' : '#cfd8dc'} label={ph.label} sub={`${ph.n} items`} />
                  ))}
                </div>
              </div>
              <div className="exd-sec">Work items by project</div>
              <div className="exd-tblwrap">
                <table className="exd-table">
                  <thead>
                    <tr>
                      <th>Delivery project</th><th className="num">Total</th><th className="num">Deliverables</th>
                      <th className="num">Activities</th><th className="num">Unclassified</th><th className="num">Done</th>
                      <th className="num">Blocked</th><th className="num">Overdue</th><th className="num">Completion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {P.projects.map((p) => (
                      <tr key={p.id}>
                        <td style={{ fontWeight: 600, color: '#263238' }}>{p.id}</td>
                        <td className="num">{p.inScope}</td>
                        <td className="num" style={{ color: 'var(--deliv)' }}>{p.deliverables}</td>
                        <td className="num" style={{ color: 'var(--activ)' }}>{p.activities}</td>
                        <td className="num" style={{ color: 'var(--faint)' }}>{p.unclassified}</td>
                        <td className="num">{p.done}</td>
                        <td className="num" style={p.blocked ? { color: 'var(--bad)', fontWeight: 700 } : undefined}>{p.blocked}</td>
                        <td className="num" style={p.overdue ? { color: 'var(--bad)', fontWeight: 700 } : undefined}>{p.overdue}</td>
                        <td className="num" style={{ fontWeight: 700, color: 'var(--accent)' }}>{p.completion}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Schedule ──────────────────────────────────────────────────────────────── */}
            <div className={`exd-panel${tab === 'schedule' ? ' on' : ''}`}>
              {(() => {
                const od = s.wiOverdue;
                const worst = od.length ? daysBetween(today, od[0]!.due) : 0;
                const badly = s.wiLateBuckets.filter((b) => b.bucket === '31-90 days' || b.bucket === '90+ days').reduce((a, b) => a + b.n, 0);
                const affected = P.projects.filter((p) => p.overdue);
                return (
                  <>
                    <div className="exd-kpis">
                      <Kpi lbl="Overdue work items" val={o.overdue} sub={`${Math.round((o.overdue / (o.wiTotal || 1)) * 100)}% of everything in scope`} c={o.overdue ? 'var(--bad)' : 'var(--good)'} />
                      <Kpi lbl="Worst slip" val={<>{worst} <small>days</small></>} sub={od.length ? od[0]!.due : '—'} c={worst > 90 ? 'var(--bad)' : 'var(--warn)'} />
                      <Kpi lbl="Overdue > 30 days" val={badly} sub="well past the plan" c={badly ? 'var(--bad)' : 'var(--good)'} />
                      <Kpi lbl="Projects affected" val={<>{affected.length} <small>/ {P.projects.length}</small></>} sub="carrying overdue work" c={affected.length ? 'var(--warn)' : 'var(--good)'} />
                      <Kpi lbl="Blocked" val={o.blocked} sub="waiting on something" c={o.blocked ? 'var(--bad)' : 'var(--good)'} />
                    </div>
                    <div className="exd-grid exd-g2">
                      <div className="exd-card">
                        <h3>How late is late?</h3>
                        <div className="cap">A week behind and a quarter behind are different problems.</div>
                        <div className="plot">
                          <Cols rows={s.wiLateBuckets} w={520} h={170}
                            colour={(r) => (r.bucket === '90+ days' ? '#c62828' : r.bucket === '31-90 days' ? '#ef6c00' : '#f59f00')} />
                        </div>
                      </div>
                      <div className="exd-card">
                        <h3>Overdue by project</h3>
                        <div className="cap">Where the slipped work actually sits.</div>
                        <HBars rows={affected.map((p) => ({ k: p.id, v: p.overdue }))} colourFn={() => 'var(--bad)'} />
                        {!affected.length ? <div className="exd-dim">Nothing overdue.</div> : null}
                      </div>
                    </div>
                    <div className="exd-sec">Oldest overdue work items</div>
                    <div className="exd-tblwrap">
                      <table className="exd-table">
                        <thead>
                          <tr><th>Work item</th><th>Project</th><th>Type</th><th>Owner</th><th>Due</th><th className="num">Days late</th><th className="num">Progress</th></tr>
                        </thead>
                        <tbody>
                          {od.slice(0, 30).map((w, i) => (
                            <tr key={`${w.id}${i}`}>
                              <td style={{ fontWeight: 600, color: '#263238' }}>{w.name}</td>
                              <td>{w.id}</td>
                              <td><span className={`tag ${w.wtype === 'Deliverable' ? 'd' : w.wtype === 'Activity' ? 'a' : 'u'}`}>{w.wtype}</span></td>
                              <td>{w.owner || '—'}</td>
                              <td className="num">{w.due}</td>
                              <td className="num" style={{ fontWeight: 700, color: 'var(--bad)' }}>{daysBetween(today, w.due)}</td>
                              <td className="num">{w.progress}%</td>
                            </tr>
                          ))}
                          {!od.length ? <tr><td colSpan={7} style={{ color: '#90a4ae' }}>Nothing overdue.</td></tr> : null}
                        </tbody>
                      </table>
                    </div>
                    {od.length > 30 ? <div className="exd-note">Showing the 30 oldest of {od.length} overdue work items.</div> : null}
                  </>
                );
              })()}
            </div>

            {/* ── Money & Effort ────────────────────────────────────────────────────────── */}
            <div className={`exd-panel${tab === 'money' ? ' on' : ''}`}>
              <div className="exd-kpis">
                <Kpi lbl="Budget planned" val={kmoney(o.planned)} sub={`${P.projects.filter((p) => p.budgetPlanned).length} projects with a budget`} c="var(--accent)" />
                <Kpi lbl="Budget actual" val={kmoney(o.actual)} sub={`${bu}% utilisation`} c={util(o.planned ? bu : null)} />
                <Kpi lbl="Variance" val={`${o.actual - o.planned > 0 ? '+' : ''}${kmoney(o.actual - o.planned)}`} sub={o.actual > o.planned ? 'over plan' : 'under plan'} c={o.actual > o.planned ? 'var(--bad)' : 'var(--good)'} />
                <Kpi lbl="Effort planned" val={`${fmtNum(o.effortPlanned)} pd`} sub="FTE × phase working days" c="var(--info)" />
                <Kpi lbl="Effort actual" val={`${fmtNum(o.effortActual)} pd`} sub={`${eu}% utilisation`} c={util(o.effortPlanned ? eu : null)} />
                <Kpi lbl="No budget set" val={P.projects.filter((p) => !p.budgetPlanned).length} sub="nothing planned yet" c={P.projects.some((p) => !p.budgetPlanned) ? 'var(--warn)' : 'var(--good)'} />
              </div>
              <div className="exd-card">
                <h3>What drives the variance</h3>
                <div className="cap">Each bar is a project&rsquo;s contribution; the last is the portfolio total. Red is over plan.</div>
                <div className="plot">
                  <Waterfall rows={P.projects.map((p) => ({ id: p.id, planned: p.budgetPlanned, actual: p.budgetActual, variance: p.variance }))} w={620} h={230} />
                </div>
              </div>
              <div className="exd-grid exd-g2">
                <div className="exd-card">
                  <h3>Planned vs. actual, by project</h3>
                  <div className="cap">The bar is the plan; the number is what has been spent.</div>
                  <HBars
                    rows={P.projects.filter((p) => p.budgetPlanned || p.budgetActual).map((p) => ({ k: p.id, v: p.budgetPlanned }))}
                    colourFn={() => 'var(--accent)'}
                    fmt={(r) => {
                      const p = P.projects.find((x) => x.id === r.k)!;
                      return `${kmoney(p.budgetActual)} / ${kmoney(p.budgetPlanned)}`;
                    }}
                  />
                </div>
                <div className="exd-card">
                  <h3>Budget by phase</h3>
                  <div className="cap">Where the money is planned across the delivery lifecycle.</div>
                  <HBars
                    rows={s.phaseSpend.map((ps) => ({ k: String(ps.label), v: Number(ps.planned) || 0 }))}
                    colourFn={() => 'var(--good)'}
                    fmt={(r) => money(r.v)}
                  />
                  {!s.phaseSpend.length ? <div className="exd-dim">No per-phase budget recorded.</div> : null}
                </div>
              </div>
            </div>

            {/* ── Actions & Risks ───────────────────────────────────────────────────────── */}
            <div className={`exd-panel${tab === 'actions' ? ' on' : ''}`}>
              {(() => {
                const acts = s.actions;
                const CLOSED = ['Closed', 'Completed', 'Cancelled'];
                const open = acts.filter((a) => !CLOSED.includes(a.status));
                const count = (fn: (a: (typeof acts)[number]) => string) => {
                  const m: Record<string, number> = {};
                  open.forEach((a) => { const k = fn(a) || '—'; m[k] = (m[k] ?? 0) + 1; });
                  return Object.keys(m).sort((a, b) => m[b]! - m[a]!).map((k) => ({ k, v: m[k]! }));
                };
                const SEV = ['Critical', 'High', 'Medium', 'Low'];
                const STS = ['Open', 'Watch', 'Mitigating', 'Resolved', 'Closed'];
                const heatRows = SEV.map((sev) => {
                  const row: Dict = { sev };
                  STS.forEach((st) => (row[st] = s.risks.filter((r) => r.sev === sev && r.status === st).length));
                  return row;
                });
                const dueSoon = open.filter((a) => a.due).sort((a, b) => String(a.due).localeCompare(String(b.due)));
                return (
                  <>
                    <div className="exd-kpis">
                      <Kpi lbl="Open actions" val={open.length} sub={`${acts.length - open.length} closed`} c="var(--accent)" />
                      <Kpi lbl="Overdue actions" val={o.actionsOverdue} sub="past their due date" c={o.actionsOverdue ? 'var(--bad)' : 'var(--good)'} />
                      <Kpi lbl="Open risks" val={o.openRisks} sub={`${o.critical} high / critical`} c={o.critical ? 'var(--bad)' : 'var(--warn)'} />
                      <Kpi lbl="Owners engaged" val={new Set(open.map((a) => a.owner).filter(Boolean)).size} sub="people carrying actions" c="var(--info)" />
                      <Kpi lbl="High priority" val={open.filter((a) => a.priority === 'Critical' || a.priority === 'High').length} sub="Critical + High" c="var(--warn)" />
                    </div>
                    <div className="exd-grid exd-g2">
                      <div className="exd-card">
                        <h3>Actions by type</h3>
                        <div className="cap">What kind of follow-up the portfolio is carrying.</div>
                        <HBars rows={count((a) => a.atype)} colourFn={() => 'var(--accent)'} />
                        {!open.length ? <div className="exd-dim">No open actions.</div> : null}
                      </div>
                      <div className="exd-card">
                        <h3>Owner load</h3>
                        <div className="cap">Who is carrying the open follow-up.</div>
                        <HBars rows={count((a) => a.owner).slice(0, 10)} colourFn={() => 'var(--info)'} />
                        {!open.length ? <div className="exd-dim">No open actions.</div> : null}
                      </div>
                    </div>
                    <div className="exd-grid exd-g2">
                      <div className="exd-card">
                        <h3>Priority mix</h3>
                        <div className="cap">Open actions only.</div>
                        <HBars
                          rows={['Critical', 'High', 'Medium', 'Low'].map((p) => ({ k: p, v: open.filter((a) => a.priority === p).length }))}
                          colourFn={(r) => (r.k === 'Critical' ? 'var(--bad)' : r.k === 'High' ? 'var(--warn)' : r.k === 'Medium' ? 'var(--accent)' : 'var(--faint)')}
                        />
                      </div>
                      <div className="exd-card">
                        <h3>Risk heat map</h3>
                        <div className="cap">Severity against status — where the unresolved weight sits.</div>
                        <Heat rows={heatRows} />
                        {!s.risks.length ? <div className="exd-dim">No risks recorded.</div> : null}
                      </div>
                    </div>
                    <div className="exd-sec">Action queue by due date</div>
                    <div className="exd-tblwrap">
                      <table className="exd-table">
                        <thead><tr><th>Action</th><th>Type</th><th>Owner</th><th>Priority</th><th>Status</th><th>Due</th><th className="num">%</th></tr></thead>
                        <tbody>
                          {dueSoon.slice(0, 25).map((a, i) => (
                            <tr key={`${a.id}${i}`}>
                              <td style={{ fontWeight: 600, color: '#263238' }}>{a.title}</td>
                              <td>{a.atype}</td>
                              <td>{a.owner || '—'}</td>
                              <td>{a.priority}</td>
                              <td>{a.status}</td>
                              <td className="num" style={a.due && a.due < today ? { color: 'var(--bad)', fontWeight: 700 } : undefined}>{a.due ?? '—'}</td>
                              <td className="num">{a.pct}%</td>
                            </tr>
                          ))}
                          {!dueSoon.length ? <tr><td colSpan={7} style={{ color: '#90a4ae' }}>No dated open actions.</td></tr> : null}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* ── Projects ──────────────────────────────────────────────────────────────── */}
            <div className={`exd-panel${tab === 'projects' ? ' on' : ''}`}>
              <div className="exd-kpis">
                <Kpi lbl="Delivery projects" val={o.count} sub={`${o.finalized} finalized`} c="var(--accent)" />
                <Kpi lbl="In flight" val={P.projects.filter((p) => p.stage === 'In flight').length} sub="finalized and moving" c="var(--good)" />
                <Kpi lbl="Not configured" val={o.noMethodology} sub="no methodology yet" c={o.noMethodology ? 'var(--warn)' : 'var(--good)'} />
                <Kpi lbl="Opportunities" val={P.projects.reduce((a, p) => a + p.opps, 0)} sub="scoped into delivery" c="var(--info)" />
              </div>
              <div className="exd-tblwrap">
                <table className="exd-table">
                  <thead>
                    <tr>
                      <th>Project</th><th>Stage</th><th>Health</th><th>Method</th><th className="num">Opps</th>
                      <th className="num">Work items</th><th className="num">Done</th><th className="num">Overdue</th>
                      <th className="num">Completion</th><th className="num">Budget</th><th className="num">Util</th>
                      <th className="num">Effort pd</th><th className="num">Risks</th><th>Next milestone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {P.projects.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <div style={{ fontWeight: 600, color: '#263238' }}>{p.id}</div>
                          {p.description ? <div style={{ fontSize: 11, color: '#90a4ae' }}>{p.description}</div> : null}
                        </td>
                        <td><span className="pill warn" style={{ background: `${STAGE_COL[STAGES.indexOf(p.stage)] ?? '#eceff1'}22`, color: STAGE_COL[STAGES.indexOf(p.stage)] ?? '#546e7a' }}>{p.stage}</span></td>
                        <td><span className={`pill ${p.status === 'Complete' ? 'complete' : p.status === 'At Risk' ? 'atrisk' : 'ontrack'}`}>{p.status}</span></td>
                        <td>{p.methodology ?? <span style={{ color: '#cfd8dc' }}>—</span>}</td>
                        <td className="num">{p.opps}</td>
                        <td className="num">{p.inScope}</td>
                        <td className="num">{p.done}</td>
                        <td className="num" style={p.overdue ? { color: 'var(--bad)', fontWeight: 700 } : undefined}>{p.overdue}</td>
                        <td className="num" style={{ fontWeight: 700, color: 'var(--accent)' }}>{p.completion}%</td>
                        <td className="num">{kmoney(p.budgetActual)} / {kmoney(p.budgetPlanned)}</td>
                        <td className="num" style={{ color: util(p.budgetPlanned ? p.budgetUtil : null), fontWeight: 700 }}>{p.budgetPlanned ? `${p.budgetUtil}%` : '—'}</td>
                        <td className="num">{fmtNum(p.effortActual)} / {fmtNum(p.effortPlanned)}</td>
                        <td className="num">{p.openRisks}{p.criticalRisks ? <span style={{ color: 'var(--bad)', fontWeight: 700 }}> ({p.criticalRisks}!)</span> : null}</td>
                        <td>{p.nextMilestone ? <><span style={{ fontWeight: 600, color: '#1976d2' }}>{p.nextMilestone.date}</span><div style={{ fontSize: 11, color: '#90a4ae' }}>{p.nextMilestone.label}</div></> : <span style={{ color: '#cfd8dc' }}>—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="exd-note">
              Budget planned = approved/derived per-phase budget; actual = vendor payments (or quick actuals).
              Effort = FTE × phase working-days (planned) vs. logged effort (actual). Risk counts exclude
              resolved/closed. Lifecycle stage is derived, not stored.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
